import { useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'
import { fetchTopStories, fetchStories, fetchSimilarStories, topStoriesQueryKey } from '../../api/storiesApi'
import type { StoryItem } from '../../types/api'
import styles from './StoryCarousel.module.css'

export type CarouselCriterion = 'similar' | 'category' | 'top'

interface StoryCarouselProps {
  criterion: CarouselCriterion
  citySlug?: string
  lang: string
  seedClusterId?: number
  category?: string
  heading?: string
  days?: number
  date?: string
}

function renderTitle(md: string): string {
  return DOMPurify.sanitize(marked.parseInline(md) as string)
}

export default function StoryCarousel({
  criterion,
  citySlug = '',
  lang,
  seedClusterId,
  category = 'all',
  heading,
  days = 7,
  date,
}: StoryCarouselProps) {
  const { t } = useTranslation()
  const trackRef = useRef<HTMLDivElement>(null)
  const { city: paramCity = '' } = useParams<{ lang: string; city: string }>()
  const navigate = useNavigate()

  // citySlug prop is preferred; fall back to route param
  const resolvedCity = citySlug || paramCity

  // For 'top' criterion use the canonical key so TanStack Query deduplicates the
  // in-flight request when NewspaperPage and StoryCarousel both mount at once.
  const queryKey =
    criterion === 'top'
      ? topStoriesQueryKey(citySlug, lang, category, date)
      : ['carousel', criterion, citySlug, lang, seedClusterId, category, days, date]

  const { data: stories, isLoading } = useQuery<StoryItem[]>({
    queryKey,
    queryFn: () => {
      if (criterion === 'similar' && seedClusterId != null) {
        return fetchSimilarStories(seedClusterId, lang, 5, days, date)
      }
      if (criterion === 'top') {
        return fetchTopStories(citySlug, lang, category === 'all' ? undefined : category, days, 10, date)
      }
      // criterion === 'category'
      return fetchStories(citySlug, lang, category, 1, 10, days, date).then(r => r.stories)
    },
    staleTime: 5 * 60 * 1000,
    enabled:
      criterion === 'similar'
        ? seedClusterId != null
        : citySlug !== '',
  })

  function scroll(dir: 'prev' | 'next') {
    const el = trackRef.current
    if (!el) return
    const cardWidth = 260 + 16
    el.scrollBy({ left: dir === 'next' ? cardWidth * 2 : -cardWidth * 2, behavior: 'smooth' })
  }

  const displayHeading = heading ?? (criterion === 'similar' ? t('more_on_this_topic') : t('top_stories'))

  return (
    <section className={styles.section} aria-label={displayHeading}>
      <div className={styles.header}>
        <h3 className={styles.heading}>{displayHeading}</h3>
        <div className={styles.navButtons}>
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => scroll('prev')}
            aria-label={t('carousel_prev')}
          >
            ‹
          </button>
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => scroll('next')}
            aria-label={t('carousel_next')}
          >
            ›
          </button>
        </div>
      </div>

      <div className={styles.track} ref={trackRef}>
        {isLoading
          ? [...Array(5)].map((_, i) => <div key={i} className={styles.skeletonCard} />)
          : (stories ?? []).filter(s => Boolean(s.ai_title)).map(story => (
              <div
                key={story.cluster_id}
                className={styles.card}
                onClick={() => navigate(`/${lang}/${resolvedCity}/story/${story.cluster_id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/${lang}/${resolvedCity}/story/${story.cluster_id}`) }}
                aria-label={story.ai_title}
              >
                {story.image_url && (
                  <img src={story.image_url} alt="" className={styles.cardImage} />
                )}
                <div className={styles.cardCategory}>{story.category}</div>
                <p
                  className={styles.cardTitle}
                  dangerouslySetInnerHTML={{ __html: renderTitle(story.ai_title) }}
                />
              </div>
            ))}
      </div>
    </section>
  )
}
